import { useCounter } from "@/lib/useCounter";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Premium KPI tile with animated counter, sparkline, and delta vs previous period.
 * Designed to feel like Stripe/Linear dashboards — dense but airy.
 */
export default function KpiTile({
  label,
  value,
  delta,
  format = "int",
  hint,
  spark = null,
  accent = "primary",   // primary | emerald | amber | rose | violet | zinc
  icon: Icon,
  testid,
}) {
  const numeric = typeof value === "number";
  const animated = useCounter(numeric ? value : 0, {
    decimals: format === "money" ? 0 : format === "pct" ? 1 : 0,
  });

  const formatted = numeric
    ? format === "money"
      ? `${Number(animated).toLocaleString("ro-RO", { maximumFractionDigits: 0 })} RON`
      : format === "pct"
      ? `${Number(animated).toFixed(1)}%`
      : Number(animated).toLocaleString("ro-RO")
    : (value ?? "—");

  const deltaSign = typeof delta === "number" ? (delta > 0.1 ? "up" : delta < -0.1 ? "down" : "flat") : null;

  return (
    <div
      className="group relative rounded-xl border border-border bg-white p-4 transition hover:border-foreground/20 hover:shadow-sm"
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {label}
        </div>
        {Icon && (
          <div className={cn("h-7 w-7 rounded-md flex items-center justify-center", `bg-${accent}-500/10`)}>
            <Icon className={cn("h-3.5 w-3.5", `text-${accent}-600`)} />
          </div>
        )}
      </div>
      <div className="mt-3 font-display text-2xl sm:text-[28px] font-bold tracking-tight leading-none tabular-nums">
        {formatted}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 min-h-[18px]">
        {deltaSign ? (
          <div
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium tabular-nums",
              deltaSign === "up" && "bg-emerald-50 text-emerald-700",
              deltaSign === "down" && "bg-rose-50 text-rose-700",
              deltaSign === "flat" && "bg-zinc-100 text-zinc-600",
            )}
          >
            {deltaSign === "up" && <ArrowUpRight className="h-3 w-3" />}
            {deltaSign === "down" && <ArrowDownRight className="h-3 w-3" />}
            {Math.abs(delta).toFixed(1)}%
          </div>
        ) : <span />}
        {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
      </div>
      {spark && spark.length > 1 && <MiniSpark data={spark} accent={accent} />}
    </div>
  );
}

function MiniSpark({ data, accent }) {
  const w = 100;
  const h = 22;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = h - ((v - min) / span) * h;
      return `${x},${y.toFixed(2)}`;
    })
    .join(" ");
  return (
    <svg className="mt-2 w-full h-5 overflow-visible" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <polyline
        points={pts}
        fill="none"
        stroke={`var(--tw-color-${accent}-500, #ec4899)`}
        strokeWidth="1.3"
        vectorEffect="non-scaling-stroke"
        className={cn(
          accent === "emerald" && "stroke-emerald-500",
          accent === "amber" && "stroke-amber-500",
          accent === "rose" && "stroke-rose-500",
          accent === "violet" && "stroke-violet-500",
          accent === "zinc" && "stroke-zinc-400",
          (!accent || accent === "primary") && "stroke-primary",
        )}
      />
    </svg>
  );
}
